import { Injectable } from '@angular/core';
import {
  AbstractPaymentMethod,
  AbstractPaymentPlatformService,
  AIWYN_PAYMENT_METHOD_VALIDATION_ERROR_MESSAGE,
  PAYMENT_METHOD_CATEGORY_LOG_NAME,
  PAYMENT_METHOD_TYPE_TO_CATEGORY,
  PAYMENT_METHODS_WITH_BILLING_ADDRESS_REQUIRED,
  PaymentMethodMode,
  PaymentPlatformPaymentData,
} from '../models';
import { BehaviorSubject, combineLatest, Observable, of, throwError } from 'rxjs';
import { StripePlatformService } from '../stripe/services';
import {
  AiwynPaymentMethod,
  BillingDetails,
  CancelPaymentPayload,
  ChargePayload,
  PaymentActionRequirement,
  PaymentMethodType,
  PaymentMethodValidationError,
  PaymentMethodValidationErrors,
  PaymentPlatform,
  PaymentPlatformBillingDetails,
  PaymentResult,
} from '@aiwyn/api';
import {
  compareStringsIgnoreCase,
  getLoadedResults,
  ignoreFalsy,
  ignoreNullish,
  isEmpty,
  Loadable,
  LoadableStatus,
  Logger,
  roundMonetaryValue,
  TrackService,
} from '@aiwyn/shared';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { Store, TypedSelector } from '@ngxs/store';
import { ConfigService, ConfigState } from '@aiwyn/config';
import { AuthService } from '@aiwyn/auth';
import { CommonSettingsState } from '@aiwyn/common/settings';

/**************************************************************** README *******************************************************************
 *
 * Since we may want to either simultaneously support multiple payment platforms or switch between payment platforms,
 * we need this service to act as a proxy to the active payment platform service.
 *
 ******************************************************************************************************************************************/

@Injectable({
  providedIn: 'root',
})
export class PaymentPlatformService implements AbstractPaymentPlatformService {
  private readonly _activePlatformService$ = new BehaviorSubject<Loadable<AbstractPaymentPlatformService>>(undefined);

  constructor(
    private readonly stripePlatformService: StripePlatformService,
    private readonly store: Store,
    private readonly configService: ConfigService,
    private readonly logger: Logger,
    private readonly authService: AuthService,
    private readonly trackService: TrackService,
  ) { }

  //#region metadata
  get platform(): Observable<PaymentPlatform> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.platform),
    );
  }

  initialize(): Observable<unknown> {
    this.logger.info('PAYMENT : PaymentPlatformService : initialize');
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.initialize()),
    );
  }

  reset(): Observable<unknown> {
    return this._activePlatformService$.pipe(
      map(activePlatformService => activePlatformService?.data),
      take(1),
      ignoreNullish(),
      switchMap(activePlatformService => activePlatformService.reset()),
      take(1),
      tap(_ => this._activePlatformService$.next(undefined)),
    );
  }

  getPlatformInitErrors(): Observable<{ message: string }> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.getPlatformInitErrors()),
    );
  }
  //#endregion

  //#region strategy specific
  setActivePlatformService(platform: PaymentPlatform): Observable<unknown> {
    this.logger.info(`PAYMENT : PaymentPlatformService : setActivePlatformService : platform = "${platform}"`);

    return (this._activePlatformService$?.value ? this._activePlatformService : of(undefined)).pipe(
      take(1),
      switchMap(activePlatformService => activePlatformService?.platform ?? of(undefined)),
      switchMap(activePlatform => {
        // If the active platform is already the platform we want to set, we don't need to do anything.
        if (activePlatform === platform) {
          return of(undefined);
        }

        this._activePlatformService$.next({ status: LoadableStatus.LOADING });
        const activePaymentPlatformService = this._marshalPaymentPlatform(platform);

        if (activePaymentPlatformService == undefined) {
          return throwError(`"${platform}" is not a supported payment platform.`);
        }

        return activePaymentPlatformService.initialize().pipe(
          tap(_ => this._activePlatformService$.next({ status: LoadableStatus.LOADED, data: activePaymentPlatformService })),
        );
      }),
    );
  }

  setDefaultActivePlatformService(): Observable<PaymentPlatform> {
    this.logger.info('PAYMENT : PaymentPlatformService : setDefaultActivePlatformService');

    return combineLatest([
      this.configService.configValuesLoaded$.pipe(ignoreFalsy()),
      this.authService.getUser().pipe(ignoreNullish()),
    ]).pipe(
      take(1),
      switchMap(_ => this.setActivePlatformService(PaymentPlatform.STRIPE)),
      switchMap(_ => this._activePlatformService$.value.data.platform),
      take(1),
    );
  }

  private get _activePlatformService(): Observable<AbstractPaymentPlatformService> {
    return getLoadedResults(this._activePlatformService$);
  }

  private _marshalPaymentPlatform(platform: PaymentPlatform): AbstractPaymentPlatformService {
    switch (platform) {
      case PaymentPlatform.STRIPE:
        return this.stripePlatformService;
      default:
        return undefined;
    }
  }
  //#endregion

  //#region payment methods
  getNewPaymentMethodTypeOptions(): Observable<PaymentMethodType[]> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.getNewPaymentMethodTypeOptions()),
    );
  }

  getSavedPaymentMethodsStatus(): Observable<LoadableStatus> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.getSavedPaymentMethodsStatus()),
    );
  }

  getSavedPaymentMethods(forceRefresh = false): Observable<AbstractPaymentMethod[]> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.getSavedPaymentMethods(forceRefresh)),
    );
  }

  getSavedPaymentMethodsExist(): Observable<boolean> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.getSavedPaymentMethodsExist()),
    );
  }

  getSavedPaymentMethod(paymentMethodId: string): Observable<AbstractPaymentMethod> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.getSavedPaymentMethod(paymentMethodId)),
    );
  }

  getDefaultPaymentMethodIdStatus(): Observable<LoadableStatus> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.getDefaultPaymentMethodIdStatus()),
    );
  }

  getDefaultPaymentMethodId(): Observable<string> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.getDefaultPaymentMethodId()),
    );
  }

  createPaymentMethod(): Observable<AbstractPaymentMethod> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.createPaymentMethod()),
      switchMap(paymentMethod => this.validatePaymentMethod(paymentMethod).pipe(
        switchMap(validationErrors => {
          return validationErrors != undefined
            ? throwError({ message: AIWYN_PAYMENT_METHOD_VALIDATION_ERROR_MESSAGE, validationErrors, paymentMethod })
            : of(paymentMethod);
        }),
      )),
      take(1), // createPaymentMethod emits endlessly without this, why?
      tap({
        next: paymentMethod => this._trackPaymentMethodCreation(paymentMethod),
        error: error => this._trackPaymentMethodCreation(error.paymentMethod, { message: error.message }),
      }),
    );
  }

  /**
   * Logs payment method creation attempts for analytics and comparison purposes.
   * This function is used exclusively for logging and does not affect application behavior.
   *
   * Tracks all payment method categories with success/failure status:
   * - Card Payment Method (credit/debit cards)
   * - Bank Debit Payment Method (ACH, US Bank Account, ACSS, BACS)
   * - Wallet Payment Method (Apple Pay, Google Pay)
   *
   * For wallet payment methods using tokenized cards, logs the wallet type (e.g., "apple_pay", "google_pay").
   * For other payment methods, logs the payment method type directly (e.g., "card", "us_bank_account").
   *
   * @param paymentMethod - The payment method being created
   * @param error - Optional error object if creation failed
   */
  private _trackPaymentMethodCreation(paymentMethod: AbstractPaymentMethod, error?: { message: string }): void {
    if (!paymentMethod?.type) {
      return;
    }

    const category = PAYMENT_METHOD_TYPE_TO_CATEGORY[paymentMethod.type];
    const displayName = PAYMENT_METHOD_CATEGORY_LOG_NAME[category];

    // For wallet payments (tokenized cards), use the wallet type; otherwise use the payment method type
    const paymentMethodIdentifier = paymentMethod.isType(PaymentMethodType.CARD) && paymentMethod.wallet
      ? paymentMethod.wallet
      : paymentMethod.type;

    this.trackService.track(
      `${displayName} Payment Method`,
      {
        type: paymentMethodIdentifier,
        creation: error ? 'failed' : 'succeeded',
        ...(isEmpty(error) ? {} : { error: error?.message }),
      },
    );
  }

  validatePaymentMethod(paymentMethod: AbstractPaymentMethod, checkForDuplicates = true): Observable<PaymentMethodValidationErrors> {
    if (paymentMethod == undefined) {
      return of(undefined);
    }

    return (checkForDuplicates ? this.getSavedPaymentMethods() : of(undefined)).pipe(
      map(savedPaymentMethods => paymentMethod.validate(
        this.configService,
        this.store.selectSnapshot(CommonSettingsState.getRestrictedUsStates),
        savedPaymentMethods,
      )),
    );
  }

  getValidationErrorMessage(validationErrors: PaymentMethodValidationErrors): string {
    if (Object.keys(validationErrors ?? {}).length === 0) {
      return undefined;
    }

    const matchingValidationErrorKey = [
      PaymentMethodValidationError.DUPLICATE,
      PaymentMethodValidationError.FUNDING,
      PaymentMethodValidationError.BRAND,
      PaymentMethodValidationError.EXPIRATION,
      PaymentMethodValidationError.COUNTRY,
      PaymentMethodValidationError.STATE,
      PaymentMethodValidationError.INCOMPLETE_BILLING_DETAILS,
    ].find(validationErrorKey => validationErrorKey in validationErrors);

    return validationErrors[matchingValidationErrorKey];
  }

  onValidPaymentMethodCreation(createdPaymentMethod: AbstractPaymentMethod): Observable<unknown> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.onValidPaymentMethodCreation(createdPaymentMethod)),
    );
  }

  onInvalidPaymentMethodCreation(error: Error): Observable<unknown> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.onInvalidPaymentMethodCreation(error)),
    );
  }

  savePaymentMethod(paymentMethodId: string): Observable<string> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.savePaymentMethod(paymentMethodId)),
      switchMap(paymentMethodId => this.getSavedPaymentMethods(true).pipe(
        map(_ => paymentMethodId),
      )),
    );
  }

  setTemporaryPaymentMethod(paymentMethod: AiwynPaymentMethod): Observable<void> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.setTemporaryPaymentMethod(paymentMethod)),
    );
  }

  removeTemporaryPaymentMethod(): Observable<unknown> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.getSavedPaymentMethodsExist().pipe(
        switchMap(savedPaymentMethodsExist => combineLatest([
          activePlatformService.removeTemporaryPaymentMethod(),
          // can't fall back on anything when there are no saved payment methods since new payment method mode is a separate flow
          activePlatformService.setPaymentMethodMode(savedPaymentMethodsExist ? PaymentMethodMode.SAVED : undefined),
        ])),
      )),
    );
  }

  getTemporaryPaymentMethod(): Observable<AbstractPaymentMethod> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.getTemporaryPaymentMethod()),
    );
  }

  setSelectedSavedPaymentMethodId(paymentMethodId: string): Observable<void> {
    // will we need to update mode here?
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.setSelectedSavedPaymentMethodId(paymentMethodId)),
    );
  }

  getSelectedPaymentMethodId(): Observable<string> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.getSelectedPaymentMethodId()),
    );
  }

  getSelectedSavedPaymentMethodId(): Observable<string> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.getSelectedSavedPaymentMethodId()),
    );
  }

  setDefaultPaymentMethod(paymentMethodId: string): Observable<void> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.setDefaultPaymentMethod(paymentMethodId)),
      take(1),
    );
  }

  deletePaymentMethod(paymentMethodId: string): Observable<unknown> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.deletePaymentMethod(paymentMethodId).pipe(
        switchMap(_ => activePlatformService.getSavedPaymentMethods(true)),
        switchMap(_ => activePlatformService.getSavedPaymentMethodsExist()),
        take(1),
        switchMap(savedPaymentMethodsExist => {
          // deleted only saved payment method, clear default payment method ID
          if (!savedPaymentMethodsExist) {
            return activePlatformService.setDefaultPaymentMethod('');
          }

          // deleted default payment method, update it to first saved payment method based on sort key
          return this.getDefaultPaymentMethodId().pipe(
            take(1),
            map(defaultPaymentMethodId => defaultPaymentMethodId === paymentMethodId),
            switchMap(defaultPaymentMethodDeleted => {
              if (!defaultPaymentMethodDeleted) {
                return of(undefined);
              }

              return this.getSavedPaymentMethods().pipe(
                take(1),
                map(savedPaymentMethods => savedPaymentMethods.sort((a, b) => compareStringsIgnoreCase(a.sortKey, b.sortKey))),
                switchMap(([firstPaymentMethod]) => this.setDefaultPaymentMethod(firstPaymentMethod.id)),
              );
            }),
          );
        }),
        take(1),
      )),
    );
  }

  /**
   * Update billing details of a payment method, reload saved payments methods after update, and return ID of updated payment method.
   * @param paymentMethodId
   * @param billingDetails
   */
  updatePaymentMethod(paymentMethodId: string, billingDetails: BillingDetails): Observable<string> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.updatePaymentMethod(paymentMethodId, billingDetails).pipe(
        switchMap(updatedPaymentMethodId => activePlatformService.getSavedPaymentMethods(true).pipe(
          map(_ => updatedPaymentMethodId),
        )),
      )),
    );
  }
  //#endregion

  //#region payments

  setSelectedBusinessEntityId(businessEntityId: number): Observable<void> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.setSelectedBusinessEntityId(businessEntityId)),
      take(1),
    );
  }

  getSelectedBusinessEntityId(): Observable<number> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.getSelectedBusinessEntityId()),
    );
  }

  /**
   * Get payment platform specific data to execute a payment.
   */
  getAdditionalPaymentData(): Observable<PaymentPlatformPaymentData> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.getAdditionalPaymentData()),
    );
  }

  makePayment(chargePayload: ChargePayload): Observable<PaymentResult> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.makePayment(chargePayload)),
    );
  }

  cancelPayment(cancelPaymentPayload: CancelPaymentPayload): Observable<unknown> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.cancelPayment(cancelPaymentPayload)),
    );
  }

  handlePaymentActionRequirement(
    paymentActionRequirement: PaymentActionRequirement,
    paymentMethodType: PaymentMethodType,
  ): Observable<Error> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.handlePaymentActionRequirement(paymentActionRequirement, paymentMethodType)),
    );
  }
  //#endregion

  //#region miscellaneous
  marshalError(error: any): Error {
    return this._activePlatformService$.value.data.marshalError(error);
  }

  marshalBillingDetails(aiwynBillingDetails: PaymentPlatformBillingDetails): BillingDetails {
    return this._activePlatformService$.value.data.marshalBillingDetails(aiwynBillingDetails);
  }

  unmarshalBillingDetails(paymentPlatformBillingDetails: BillingDetails): PaymentPlatformBillingDetails {
    return this._activePlatformService$.value.data.unmarshalBillingDetails(paymentPlatformBillingDetails);
  }

  getCardRestrictionsHint(): Observable<string | undefined> {
    return getLoadedResults(this.store.select(ConfigState.getConfigsLoadable)).pipe(
      map(configs => this.marshalCardRestrictionsHintMessage(
        configs.excludeAmexProcessingEnabled,
        configs.excludeDebitAndPrepaidProcessing,
      )),
    );
  }

  private marshalCardRestrictionsHintMessage(excludeAmexProcessingEnabled: boolean, excludeDebitAndPrepaidProcessing: boolean): string | undefined {
    if (excludeAmexProcessingEnabled && excludeDebitAndPrepaidProcessing) {
      return 'American Express, debit, and prepaid cards are not accepted.';
    }

    if (excludeAmexProcessingEnabled) {
      return 'We accept Visa, Mastercard, and Discover.';
    }

    if (excludeDebitAndPrepaidProcessing) {
      return 'Debit and prepaid cards are not accepted.';
    }

    return undefined;
  }

  getProcessingFeeHint(rateSelector: TypedSelector<number>): Observable<string> {
    return this.store.select(rateSelector).pipe(
      ignoreNullish(),
      map(processingFeeRate => roundMonetaryValue(processingFeeRate * 100)),
      map(processingFeePercent => `Credit card payments incur a ${processingFeePercent}% ${this.configService.processingFeeLabel.replace(/^[A-Z]/, c => c.toLowerCase())} and are subject to local regulations.`),
    );
  }
  //#endregion

  //#region payment method details
  setNewPaymentMethodType(paymentMethodType: PaymentMethodType): Observable<void> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.setNewPaymentMethodType(paymentMethodType)),
    );
  }

  getNewPaymentMethodType(): Observable<PaymentMethodType> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.getNewPaymentMethodType()),
    );
  }

  setPaymentMethodMode(paymentMethodMode: PaymentMethodMode): Observable<void> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.setPaymentMethodMode(paymentMethodMode)),
    );
  }

  getPaymentMethodMode(): Observable<PaymentMethodMode> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.getPaymentMethodMode()),
    );
  }

  setNewBillingDetails(billingDetails: BillingDetails): Observable<void> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.setNewBillingDetails(billingDetails)),
    );
  }

  removeNewBillingDetails(): Observable<void> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.removeNewBillingDetails()),
    );
  }

  getNewBillingDetails(): Observable<BillingDetails> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.getNewBillingDetails()),
    );
  }

  getNewPaymentMethodFormComplete(): Observable<boolean> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => activePlatformService.getNewPaymentMethodFormComplete()),
    );
  }
  //#endregion

  //#region composite selectors
  getSelectedPaymentMethod(): Observable<AbstractPaymentMethod> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => combineLatest([
        activePlatformService.getPaymentMethodMode(),
        activePlatformService.getSelectedPaymentMethodId(),
      ]).pipe(
        switchMap(([paymentMethodMode, selectedPaymentMethodId]) => {
          if (
            [undefined, null, PaymentMethodMode.NEW, PaymentMethodMode.SET_UP_LATER, PaymentMethodMode.NONE].includes(paymentMethodMode)
          ) {
            return of(undefined);
          }

          return paymentMethodMode === PaymentMethodMode.TEMPORARY
            ? activePlatformService.getTemporaryPaymentMethod()
            : activePlatformService.getSavedPaymentMethod(selectedPaymentMethodId);
        }),
      )),
    );
  }

  isAddressRequired(): Observable<boolean> {
    return this._activePlatformService.pipe(
      switchMap(activePlatformService => {
        return activePlatformService.getPaymentMethodMode().pipe(
          switchMap(paymentMethodMode => {
            if ([undefined, null, PaymentMethodMode.SET_UP_LATER, PaymentMethodMode.NONE].includes(paymentMethodMode)) {
              return of(false);
            }

            if (paymentMethodMode === PaymentMethodMode.NEW) {
              return activePlatformService.getNewPaymentMethodType().pipe(
                map(paymentMethodType => PAYMENT_METHODS_WITH_BILLING_ADDRESS_REQUIRED.includes(paymentMethodType)),
              );
            }

            return this.getSelectedPaymentMethod().pipe(
              map(selectedPaymentMethod => {
                if (selectedPaymentMethod == undefined) {
                  this.logger.warn('Selected payment method is undefined when determining if address is required.');
                  return false;
                }

                return this.configService.levelThreeDataEnabled && selectedPaymentMethod.billingDetailsIncomplete;
              }),
            );
          }),
        );
      }),
    );
  }
  //#endregion
}
